/*
 * File Path: client/src/api/userService.js
 * Description: Service functions for user profile, contact information, and emergency contact management.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.0.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-01
 */

import { apiClient, handleApiError } from './apiClient';

// --- Profile ---

/**
 * Fetches the profile data for the currently logged-in user.
 * @returns {Promise<object>} Profile data object.
 * @throws {Error} Custom error on failure.
 */
export const fetchMyProfile = async () => {
    try {
        // Endpoint: GET /api/auth/users/me/profile
        const response = await apiClient.get('/auth/users/me/profile');
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Updates the profile data for the currently logged-in user.
 * @param {object} profileData - Data to update (e.g., { first_name, last_name, ... }).
 * @returns {Promise<object>} Updated profile data object.
 * @throws {Error} Custom error on failure.
 */
export const updateMyProfile = async (profileData) => {
    try {
        // Endpoint: PUT /api/auth/users/me/profile
        const response = await apiClient.put('/auth/users/me/profile', profileData);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

// --- Contact Info ---

/**
 * Fetches the contact information for the currently logged-in user.
 * @returns {Promise<object>} Contact info object.
 * @throws {Error} Custom error on failure.
 */
export const fetchMyContactInfo = async () => {
    try {
        // Endpoint: GET /api/auth/users/me/contact
        const response = await apiClient.get('/auth/users/me/contact');
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Updates the contact information for the currently logged-in user.
 * @param {object} contactData - Data to update (e.g., { email_secondary, address_street, ... }).
 * @returns {Promise<object>} Updated contact info object.
 * @throws {Error} Custom error on failure.
 */
export const updateMyContactInfo = async (contactData) => {
    try {
        // Endpoint: PUT /api/auth/users/me/contact
        const response = await apiClient.put('/auth/users/me/contact', contactData);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

// --- Secondary Email Verification ---

/**
 * Confirms secondary email verification using a token.
 * @param {string} token - The verification token from the email link.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure (e.g., invalid/expired token).
 */
export const confirmSecondaryEmailVerification = async (token) => {
    try {
        // Endpoint: GET /api/auth/verify-secondary-email/:token
        // This call might not need standard auth headers if token is sufficient
        const response = await apiClient.get(`/auth/verify-secondary-email/${token}`);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

// --- Emergency Contacts ---

/**
 * Fetches all emergency email contacts for the logged-in user.
 * @returns {Promise<Array<object>>} Array of emergency email objects.
 * @throws {Error} Custom error on failure.
 */
export const fetchEmergencyEmails = async () => {
    try {
        // Endpoint: GET /api/auth/users/me/emergency-emails
        const response = await apiClient.get('/auth/users/me/emergency-emails');
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Fetches all emergency phone contacts for the logged-in user.
 * @returns {Promise<Array<object>>} Array of emergency phone objects.
 * @throws {Error} Custom error on failure.
 */
export const fetchEmergencyPhones = async () => {
    try {
        // Endpoint: GET /api/auth/users/me/emergency-phones
        const response = await apiClient.get('/auth/users/me/emergency-phones');
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Adds a new emergency email contact for the logged-in user.
 * @param {object} payload - { name, relationship, email, language? }
 * @returns {Promise<object>} The newly created emergency email object.
 * @throws {Error} Custom error on failure.
 */
export const addEmergencyEmail = async (payload) => {
    try {
        // Endpoint: POST /api/auth/users/me/emergency-emails
        const response = await apiClient.post('/auth/users/me/emergency-emails', payload);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Adds a new emergency phone contact for the logged-in user.
 * @param {object} payload - { name, relationship, phone_number, language? }
 * @returns {Promise<object>} The newly created emergency phone object.
 * @throws {Error} Custom error on failure.
 */
export const addEmergencyPhone = async (payload) => {
    try {
        // Endpoint: POST /api/auth/users/me/emergency-phones
        const response = await apiClient.post('/auth/users/me/emergency-phones', payload);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Deletes an emergency email contact by its ID.
 * @param {number|string} id - The ID of the emergency email contact to delete.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const deleteEmergencyEmail = async (id) => {
    try {
        // Endpoint: DELETE /api/auth/users/me/emergency-emails/:id
        const response = await apiClient.delete(`/auth/users/me/emergency-emails/${id}`);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Deletes an emergency phone contact by its ID.
 * @param {number|string} id - The ID of the emergency phone contact to delete.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const deleteEmergencyPhone = async (id) => {
     try {
        // Endpoint: DELETE /api/auth/users/me/emergency-phones/:id
        const response = await apiClient.delete(`/auth/users/me/emergency-phones/${id}`);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

// --- Emergency Contact Verification ---

/**
 * Triggers sending a verification email for a specific emergency email contact.
 * @param {number|string} emergencyEmailId - The ID of the emergency email contact.
 * @param {string} language - The preferred language for the email.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const sendEmergencyEmailVerification = async (emergencyEmailId, language) => {
    try {
        // Endpoint: POST /api/auth/users/me/emergency-emails/:id/send-verification
        const response = await apiClient.post(`/auth/users/me/emergency-emails/${emergencyEmailId}/send-verification`, { language });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Verifies an emergency email contact using a token.
 * @param {string} token - The verification token from the email link.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const verifyEmergencyEmail = async (token) => {
    try {
        // Endpoint: GET /api/auth/verify-emergency-email/:token
        // This call might not need standard auth headers if token is sufficient
        const response = await apiClient.get(`/auth/verify-emergency-email/${token}`);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Triggers sending a verification code (e.g., SMS) for a specific emergency phone contact.
 * NOTE: Requires backend SMS gateway implementation.
 * @param {number|string} emergencyPhoneId - The ID of the emergency phone contact.
 * @param {string} language - The preferred language for the SMS.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const sendEmergencyPhoneVerification = async (emergencyPhoneId, language) => {
    try {
        // Endpoint: POST /api/auth/users/me/emergency-phones/:id/send-verification
        const response = await apiClient.post(`/auth/users/me/emergency-phones/${emergencyPhoneId}/send-verification`, { language });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Verifies an emergency phone contact using a code received via SMS/call.
 * NOTE: Requires backend implementation.
 * @param {number|string} emergencyPhoneId - The ID of the emergency phone contact.
 * @param {string} code - The verification code entered by the user.
 * @returns {Promise<object>} Backend response on success.
 * @throws {Error} Custom error on failure.
 */
export const verifyEmergencyPhone = async (emergencyPhoneId, code) => {
    try {
        // Endpoint: POST /api/auth/users/me/emergency-phones/:id/verify (Example)
        const response = await apiClient.post(`/auth/users/me/emergency-phones/${emergencyPhoneId}/verify`, { code });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

// --- User Search ---

/**
 * Searches for users based on username, email, or phone number.
 * @param {string} query - The search term.
 * @returns {Promise<Array<object>>} Array of user objects { uid, username, email?, phone? }.
 * @throws {Error} Custom error on failure.
 */
export const searchUsers = async (query) => {
    try {
        // Endpoint: GET /api/auth/users/search?q=<query>
        const response = await apiClient.get('/auth/users/search', {
            params: { q: query } // Send search term as query parameter 'q'
        });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};
